import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Zap, Flame, RefreshCw, Loader2, Smartphone, Wifi, ArrowLeftRight, Sparkles, Briefcase, Plug, Save } from "lucide-react";

export const ICONS = { SWITCH_LUCE: Zap, SWITCH_GAS: Flame, SUBENTRO_LUCE: Zap, SUBENTRO_GAS: Flame, RVC: RefreshCw, VCF_LUCE: ArrowLeftRight, VCF_GAS: ArrowLeftRight,
  SIM: Smartphone, FIBRA: Wifi, PRIMA_ATT_LUCE: Sparkles, PRIMA_ATT_GAS: Sparkles, SWITCH_GAS_BUSINESS: Briefcase, SWITCH_ELE_BUSINESS: Briefcase, ALLACCIO_ATTIVAZIONE: Plug };

const clean = (v) => String(v).replace(/[^0-9]/g, "");

export default function ProductionGrid({ ctx, officeId, disabled, onSaved }) {
  const [form, setForm] = useState({});
  const [local, setLocal] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const current = local || ctx.my_by_type_today || {};

  useEffect(() => { setLocal(null); setForm({ ...(ctx.my_by_type_today || {}) }); }, [ctx]);

  const dirty = ctx.types.some((t) => Number(form[t.code] ?? 0) !== (current[t.code] ?? 0));

  const save = async () => {
    if (savingRef.current || !dirty) return;
    savingRef.current = true; setSaving(true);
    try {
      const by_type = Object.fromEntries(ctx.types.map((t) => [t.code, Number(form[t.code] ?? 0)]));
      const { data } = await api.put("/monitoring/summary", {
        date: ctx.date, office_id: officeId || undefined, by_type, expected: { by_type: current },
        reason: note.trim() ? `Produzione giornaliera: ${note.trim()}` : "Aggiornamento produzione giornaliera",
      });
      if (data.unchanged) toast.info("Nessuna variazione: le quantità erano già queste");
      else toast.success("Produzione salvata: totali, dashboard, obiettivi e badge aggiornati");
      setLocal({ ...data.by_type }); setForm({ ...data.by_type });
      setNote("");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Salvataggio non riuscito: riprova");
      if (e.response?.status === 409) onSaved?.();
    } finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-border p-3" data-testid="production-block">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-700">Operazioni commerciali di oggi — {ctx.operator_name}</h3>
        <span className="text-xs text-slate-500">Inserisci i <b>totali della giornata</b>: sostituiscono i valori già registrati, non si sommano.</span>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4" data-testid="production-grid">
        {ctx.types.map((t) => {
          const Ic = ICONS[t.code] || Zap;
          const val = form[t.code] ?? 0;
          const changed = Number(val) !== (current[t.code] ?? 0);
          return (
            <label key={t.code} className={`flex items-center gap-3 rounded-lg border-2 p-2.5 transition-colors ${changed ? "border-brand bg-brand/5" : "border-border bg-white"}`} data-testid={`prod-card-${t.code}`}>
              <Ic className="h-5 w-5 shrink-0 text-brand" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold leading-tight text-slate-800 break-words">{t.name.split(" — ")[0]}</span>
                <span className="block text-[11px] text-slate-500">registrate oggi: <b data-testid={`prod-current-${t.code}`}>{current[t.code] ?? 0}</b></span>
              </span>
              <input type="number" inputMode="numeric" min={0} step={1} value={val} disabled={disabled}
                onChange={(e) => setForm((f) => ({ ...f, [t.code]: clean(e.target.value) }))}
                onFocus={(e) => e.target.select()}
                className="h-11 w-20 shrink-0 rounded-md border border-border bg-white text-center text-xl font-bold text-slate-800 focus:border-brand focus:outline-none disabled:bg-slate-50"
                data-testid={`prod-qty-${t.code}`} />
            </label>
          );
        })}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota facoltativa (es. correzione pomeriggio)" disabled={disabled}
          className="h-10 w-full rounded-md border border-border px-2 text-sm sm:flex-1" data-testid="prod-note" />
        <Button onClick={save} disabled={disabled || saving || !dirty} className="h-12 gap-2 px-6 text-base font-semibold" data-testid="prod-save">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} SALVA / AGGIORNA PRODUZIONE
        </Button>
      </div>
      {!dirty && <p className="mt-1 text-[11px] text-slate-400">Nessuna modifica da salvare: i campi mostrano le quantità già registrate oggi.</p>}
    </div>
  );
}
